import {
  APP_DISPLAY_NAME,
  type MailConnection,
  type MailConnectionProbe,
  type MailInboundMode,
  type MailOutboundMode,
  type UpdateMailConnectionRequest,
} from '@gb-ticket/shared-types';
import { Loader2Icon } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePageTitle } from '@/hooks/use-page-title';
import { ApiRequestError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { PlatformPageHeader } from '@/platform/components/platform-page-header';
import { MailWorkspacePanels } from '@/platform/features/mail/mail-workspace-panels';
import {
  useMailConnection,
  usePollMailNow,
  useProbeMailConnection,
  useTestMailConnection,
  useUpdateMailConnection,
} from '@/platform/features/mail/use-mail-connection';

type Draft = {
  outboundMode: MailOutboundMode;
  fromName: string;
  fromAddress: string;
  replyTo: string;
  organizationName: string;
  logoUrl: string;
  smtpHost: string;
  smtpPort: string;
  smtpSecure: boolean;
  smtpUser: string;
  graphTenantId: string;
  graphClientId: string;
  graphMailbox: string;
  inboundMode: MailInboundMode;
  imapHost: string;
  imapPort: string;
  imapSecure: boolean;
  imapUser: string;
  imapMailbox: string;
};

type SecretDraft = {
  smtpPassword: string;
  clearSmtpPassword: boolean;
  graphClientSecret: string;
  clearGraphClientSecret: boolean;
  imapPassword: string;
  clearImapPassword: boolean;
};

const OUTBOUND_LABELS: Record<MailOutboundMode, string> = {
  off: 'Aus',
  smtp: 'SMTP',
  microsoft_graph: 'Microsoft 365',
};

const INBOUND_LABELS: Record<MailInboundMode, string> = {
  off: 'Aus',
  microsoft_graph: 'Microsoft 365',
  imap: 'IMAP',
};

export function MailAdminPage() {
  usePageTitle('E-Mail');
  const connection = useMailConnection();

  return (
    <section className="space-y-6">
      <PlatformPageHeader
        title="E-Mail"
        description="Versand für das ganze Portal und Empfang ins Helpdesk-Postfach."
      />

      {connection.isPending && (
        <p className="flex items-center gap-2 text-muted-foreground">
          <Loader2Icon className="size-4 animate-spin" aria-hidden />
          Verbindung wird geladen …
        </p>
      )}

      {connection.isError && (
        <p role="alert" className="text-sm text-destructive">
          {connection.error instanceof ApiRequestError
            ? connection.error.message
            : 'Die E-Mail-Verbindung konnte nicht geladen werden.'}
        </p>
      )}

      {connection.data && (
        <MailConnectionForm key={connection.data.updatedAt} connection={connection.data} />
      )}
    </section>
  );
}

function MailConnectionForm({ connection }: { connection: MailConnection }) {
  const update = useUpdateMailConnection();
  const probe = useProbeMailConnection();
  const testMail = useTestMailConnection();
  const pollNow = usePollMailNow();
  const [draft, setDraft] = useState<Draft>(() => toDraft(connection));
  const [secrets, setSecrets] = useState<SecretDraft>({
    smtpPassword: '',
    clearSmtpPassword: false,
    graphClientSecret: '',
    clearGraphClientSecret: false,
    imapPassword: '',
    clearImapPassword: false,
  });
  const [probeResult, setProbeResult] = useState<MailConnectionProbe | null>(null);

  const busy = update.isPending || probe.isPending || testMail.isPending || pollNow.isPending;
  const graphInOutbound = draft.outboundMode === 'microsoft_graph';
  const graphInInbound = !graphInOutbound && draft.inboundMode === 'microsoft_graph';

  function patch(next: Partial<Draft>) {
    setDraft((current) => ({ ...current, ...next }));
  }

  function patchSecrets(next: Partial<SecretDraft>) {
    setSecrets((current) => ({ ...current, ...next }));
  }

  async function save() {
    let body: UpdateMailConnectionRequest;
    try {
      body = toRequest(draft, secrets, connection);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Die Eingaben sind unvollständig.');
      return;
    }

    try {
      await update.mutateAsync(body);
      setSecrets({
        smtpPassword: '',
        clearSmtpPassword: false,
        graphClientSecret: '',
        clearGraphClientSecret: false,
        imapPassword: '',
        clearImapPassword: false,
      });
      setProbeResult(null);
      toast.success('Die E-Mail-Verbindung wurde gespeichert.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError ? error.message : 'Die Änderung wurde nicht gespeichert.',
      );
    }
  }

  async function check() {
    try {
      const result = await probe.mutateAsync();
      setProbeResult(result);
      if (result.ok) {
        toast.success(result.detail || 'Verbindung geprüft.');
      } else {
        toast.error(result.detail || 'Die Prüfung ist fehlgeschlagen.');
      }
    } catch (error) {
      setProbeResult(null);
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Verbindung konnte nicht geprüft werden.',
      );
    }
  }

  async function sendTest() {
    try {
      const result = await testMail.mutateAsync();
      toast.success(`Testmail an ${result.to} wurde gesendet.`);
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Die Testmail konnte nicht gesendet werden.',
      );
    }
  }

  async function fetchNow() {
    try {
      await pollNow.mutateAsync();
      toast.success('Abruf wurde eingereiht.');
    } catch (error) {
      toast.error(
        error instanceof ApiRequestError
          ? error.message
          : 'Der Abruf konnte nicht gestartet werden.',
      );
    }
  }

  const addressValue = graphInOutbound ? draft.graphMailbox : draft.fromAddress;
  const addressLabel = graphInOutbound ? 'Postfach / Absenderadresse' : 'Absenderadresse';

  return (
    <form
      className="space-y-6"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <StatusCard connection={connection} />

      <Card>
        <CardHeader>
          <CardTitle>Versand</CardTitle>
          <CardDescription>
            Ein Speichern schreibt die ganze Verbindung. Ein leeres Kennwort-Feld behält den
            gespeicherten Wert.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="mail-from-name"
              label="Absendername"
              value={draft.fromName}
              disabled={busy}
              onChange={(fromName) => patch({ fromName })}
            />
            <Field
              id="mail-from-address"
              label={addressLabel}
              value={addressValue}
              disabled={busy}
              onChange={(value) =>
                graphInOutbound
                  ? patch({ graphMailbox: value, fromAddress: value })
                  : patch({ fromAddress: value })
              }
            />
            <Field
              id="mail-reply-to"
              label="Antwortadresse"
              value={draft.replyTo}
              disabled={busy}
              hint="Leer lassen, wenn Antworten an die Absenderadresse gehen sollen."
              onChange={(replyTo) => patch({ replyTo })}
            />
            <div>
              <Label htmlFor="mail-outbound-mode">Art</Label>
              <Select
                value={draft.outboundMode}
                disabled={busy}
                onValueChange={(value) => patch({ outboundMode: value as MailOutboundMode })}
              >
                <SelectTrigger id="mail-outbound-mode" className="mt-2 w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Aus</SelectItem>
                  <SelectItem value="smtp">SMTP</SelectItem>
                  <SelectItem value="microsoft_graph">Microsoft 365</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {draft.outboundMode === 'off' && (
            <p className="text-sm text-amber-800 dark:text-amber-200">
              Versand aus: Mails erscheinen nur im Backend-Log.
            </p>
          )}

          {draft.outboundMode === 'smtp' && (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                SMTP bei Microsoft 365: smtp.office365.com, Port 587, Verschlüsselung aus
                (STARTTLS).
              </p>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  id="mail-smtp-host"
                  label="SMTP-Host"
                  value={draft.smtpHost}
                  disabled={busy}
                  onChange={(smtpHost) => patch({ smtpHost })}
                />
                <Field
                  id="mail-smtp-port"
                  label="Port"
                  value={draft.smtpPort}
                  disabled={busy}
                  inputMode="numeric"
                  onChange={(smtpPort) => patch({ smtpPort })}
                />
                <Field
                  id="mail-smtp-user"
                  label="Benutzer"
                  value={draft.smtpUser}
                  disabled={busy}
                  onChange={(smtpUser) => patch({ smtpUser })}
                />
                <SecretField
                  id="mail-smtp-password"
                  label="Kennwort"
                  value={secrets.smtpPassword}
                  set={connection.smtpPasswordSet}
                  clearing={secrets.clearSmtpPassword}
                  disabled={busy}
                  onChange={(smtpPassword) =>
                    patchSecrets({ smtpPassword, clearSmtpPassword: false })
                  }
                  onClear={() => patchSecrets({ smtpPassword: '', clearSmtpPassword: true })}
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox
                  checked={draft.smtpSecure}
                  disabled={busy}
                  onCheckedChange={(value) => patch({ smtpSecure: value === true })}
                />
                Verschlüsselte Verbindung (TLS von Beginn an)
              </label>
            </div>
          )}

          {graphInOutbound && (
            <GraphFields
              draft={draft}
              secrets={secrets}
              secretSet={connection.graphClientSecretSet}
              busy={busy}
              showMailbox={false}
              onPatch={patch}
              onPatchSecrets={patchSecrets}
            />
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Empfang</CardTitle>
          <CardDescription>
            Eingehende Mails werden zu Helpdesk-Tickets. Die Zuordnung über Message-ID und
            Betreffnummer bleibt unverändert.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <Label htmlFor="mail-inbound-mode">Art</Label>
            <Select
              value={draft.inboundMode}
              disabled={busy}
              onValueChange={(value) => patch({ inboundMode: value as MailInboundMode })}
            >
              <SelectTrigger id="mail-inbound-mode" className="mt-2 w-full max-w-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Aus</SelectItem>
                <SelectItem value="microsoft_graph">Microsoft 365</SelectItem>
                <SelectItem value="imap">IMAP</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {graphInInbound && (
            <GraphFields
              draft={draft}
              secrets={secrets}
              secretSet={connection.graphClientSecretSet}
              busy={busy}
              showMailbox
              onPatch={patch}
              onPatchSecrets={patchSecrets}
            />
          )}

          {draft.inboundMode === 'microsoft_graph' && graphInOutbound && (
            <p className="text-sm text-muted-foreground">
              Die Microsoft-365-Zugangsdaten stehen in der Versand-Karte und gelten auch für den
              Empfang.
            </p>
          )}

          {draft.inboundMode === 'imap' && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                id="mail-imap-host"
                label="IMAP-Host"
                value={draft.imapHost}
                disabled={busy}
                onChange={(imapHost) => patch({ imapHost })}
              />
              <Field
                id="mail-imap-port"
                label="Port"
                value={draft.imapPort}
                disabled={busy}
                inputMode="numeric"
                onChange={(imapPort) => patch({ imapPort })}
              />
              <Field
                id="mail-imap-user"
                label="Benutzer"
                value={draft.imapUser}
                disabled={busy}
                onChange={(imapUser) => patch({ imapUser })}
              />
              <SecretField
                id="mail-imap-password"
                label="Kennwort"
                value={secrets.imapPassword}
                set={connection.imapPasswordSet}
                clearing={secrets.clearImapPassword}
                disabled={busy}
                onChange={(imapPassword) =>
                  patchSecrets({ imapPassword, clearImapPassword: false })
                }
                onClear={() => patchSecrets({ imapPassword: '', clearImapPassword: true })}
              />
              <Field
                id="mail-imap-mailbox"
                label="Postfachordner"
                value={draft.imapMailbox}
                disabled={busy}
                onChange={(imapMailbox) => patch({ imapMailbox })}
              />
              <label className="flex items-center gap-2 self-end text-sm sm:pb-2">
                <Checkbox
                  checked={draft.imapSecure}
                  disabled={busy}
                  onCheckedChange={(value) => patch({ imapSecure: value === true })}
                />
                Verschlüsselte Verbindung
              </label>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Erscheinungsbild der Mail</CardTitle>
          <CardDescription>
            Firmenname und Logo erscheinen in jeder ausgehenden Nachricht.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field
              id="mail-org-name"
              label="Firmenname"
              value={draft.organizationName}
              disabled={busy}
              onChange={(organizationName) => patch({ organizationName })}
            />
            <Field
              id="mail-logo-url"
              label="Logo-Adresse"
              value={draft.logoUrl}
              disabled={busy}
              hint="Leer lässt die bisherige Auflösung über die Serverkonfiguration."
              onChange={(logoUrl) => patch({ logoUrl })}
            />
          </div>
          <div className="rounded-md border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            <p>
              Alt-Text des Logos:{' '}
              <span className="text-foreground">
                {draft.organizationName.trim() || 'Gebrüder Becker'}
              </span>
            </p>
            <p className="mt-2">
              Fußzeile:{' '}
              <span className="text-foreground">
                Diese Nachricht wurde automatisch vom {APP_DISPLAY_NAME} der{' '}
                {draft.organizationName.trim() || 'Gebrüder Becker'} versendet.
              </span>
            </p>
          </div>
        </CardContent>
      </Card>

      <MailWorkspacePanels />

      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" disabled={busy}>
          {update.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Speichern
        </Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => void check()}>
          {probe.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Verbindung prüfen
        </Button>
        <Button type="button" variant="outline" disabled={busy} onClick={() => void sendTest()}>
          {testMail.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
          Test an mich
        </Button>
        {draft.inboundMode !== 'off' && (
          <Button type="button" variant="outline" disabled={busy} onClick={() => void fetchNow()}>
            {pollNow.isPending && <Loader2Icon className="animate-spin" aria-hidden />}
            Jetzt abrufen
          </Button>
        )}
      </div>

      {probeResult && (
        <p role="status" className="text-sm">
          {probeResult.ok ? 'Prüfung erfolgreich.' : 'Prüfung fehlgeschlagen.'} {probeResult.detail}
        </p>
      )}

      <p className="text-xs text-muted-foreground">
        Zuletzt geändert: {formatDateTime(connection.updatedAt)}. Die Prüfung gilt für die
        gespeicherte Verbindung.
      </p>
    </form>
  );
}

function StatusCard({ connection }: { connection: MailConnection }) {
  const lastError = connection.lastOutboundError ?? connection.lastInboundError;
  const lastOk = connection.lastOutboundAt ?? connection.lastInboundAt;
  return (
    <Card>
      <CardHeader>
        <CardTitle>Stand</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <p>
          Versand: <span className="font-medium">{OUTBOUND_LABELS[connection.outboundMode]}</span>
        </p>
        <p>
          Empfang: <span className="font-medium">{INBOUND_LABELS[connection.inboundMode]}</span>
        </p>
        <p>
          {lastError
            ? `Letzter Fehler: ${lastError}`
            : lastOk
              ? `Zuletzt erfolgreich: ${formatDateTime(lastOk)}`
              : 'Noch kein erfolgreicher Versand oder Abruf.'}
        </p>
        {connection.outboundMode === 'off' && (
          <p className="text-amber-800 dark:text-amber-200">Mails erscheinen nur im Backend-Log.</p>
        )}
      </CardContent>
    </Card>
  );
}

function GraphFields({
  draft,
  secrets,
  secretSet,
  busy,
  showMailbox,
  onPatch,
  onPatchSecrets,
}: {
  draft: Draft;
  secrets: SecretDraft;
  secretSet: boolean;
  busy: boolean;
  showMailbox: boolean;
  onPatch: (next: Partial<Draft>) => void;
  onPatchSecrets: (next: Partial<SecretDraft>) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Graph: App-Registrierung mit Mail.Send, bei Empfang zusätzlich Mail.Read. Das ist nicht die
        Anmeldung.
      </p>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="mail-graph-tenant"
          label="Tenant-ID"
          value={draft.graphTenantId}
          disabled={busy}
          onChange={(graphTenantId) => onPatch({ graphTenantId })}
        />
        <Field
          id="mail-graph-client"
          label="Anwendungs-ID"
          value={draft.graphClientId}
          disabled={busy}
          onChange={(graphClientId) => onPatch({ graphClientId })}
        />
        <SecretField
          id="mail-graph-secret"
          label="Anwendungsgeheimnis"
          value={secrets.graphClientSecret}
          set={secretSet}
          clearing={secrets.clearGraphClientSecret}
          disabled={busy}
          onChange={(graphClientSecret) =>
            onPatchSecrets({ graphClientSecret, clearGraphClientSecret: false })
          }
          onClear={() => onPatchSecrets({ graphClientSecret: '', clearGraphClientSecret: true })}
        />
        {showMailbox && (
          <Field
            id="mail-graph-mailbox"
            label="Postfach"
            value={draft.graphMailbox}
            disabled={busy}
            onChange={(graphMailbox) => onPatch({ graphMailbox })}
          />
        )}
      </div>
    </div>
  );
}

function SecretField({
  id,
  label,
  value,
  set,
  clearing,
  disabled,
  onChange,
  onClear,
}: {
  id: string;
  label: string;
  value: string;
  set: boolean;
  clearing: boolean;
  disabled: boolean;
  onChange: (value: string) => void;
  onClear: () => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="password"
        name={id}
        autoComplete="new-password"
        className="mt-2"
        value={value}
        disabled={disabled}
        placeholder={clearing ? 'Wird entfernt' : set ? 'Gespeichert' : 'Nicht gesetzt'}
        onChange={(event) => onChange(event.target.value)}
      />
      {set && !clearing && (
        <Button type="button" variant="outline" disabled={disabled} onClick={onClear}>
          Gespeichertes Kennwort entfernen
        </Button>
      )}
    </div>
  );
}

function Field({
  id,
  label,
  value,
  hint,
  disabled,
  inputMode,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  hint?: string;
  disabled: boolean;
  inputMode?: 'numeric';
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        className="mt-2"
        value={value}
        disabled={disabled}
        inputMode={inputMode}
        onChange={(event) => onChange(event.target.value)}
      />
      {hint && <p className="mt-1 text-sm text-muted-foreground">{hint}</p>}
    </div>
  );
}

function toDraft(connection: MailConnection): Draft {
  return {
    outboundMode: connection.outboundMode,
    fromName: connection.fromName,
    fromAddress: connection.fromAddress,
    replyTo: connection.replyTo,
    organizationName: connection.organizationName,
    logoUrl: connection.logoUrl,
    smtpHost: connection.smtpHost,
    smtpPort: String(connection.smtpPort),
    smtpSecure: connection.smtpSecure,
    smtpUser: connection.smtpUser,
    graphTenantId: connection.graphTenantId,
    graphClientId: connection.graphClientId,
    graphMailbox: connection.graphMailbox,
    inboundMode: connection.inboundMode,
    imapHost: connection.imapHost,
    imapPort: String(connection.imapPort),
    imapSecure: connection.imapSecure,
    imapUser: connection.imapUser,
    imapMailbox: connection.imapMailbox,
  };
}

function toRequest(
  draft: Draft,
  secrets: SecretDraft,
  connection: MailConnection,
): UpdateMailConnectionRequest {
  const smtpPort = parsePort(draft.smtpPort, 'SMTP-Port');
  const imapPort = parsePort(draft.imapPort, 'IMAP-Port');
  const graphMailbox =
    draft.outboundMode === 'microsoft_graph'
      ? draft.graphMailbox.trim() || draft.fromAddress.trim()
      : draft.graphMailbox.trim();
  const fromAddress =
    draft.outboundMode === 'microsoft_graph' ? graphMailbox : draft.fromAddress.trim();

  const body: UpdateMailConnectionRequest = {
    outboundMode: draft.outboundMode,
    fromName: draft.fromName.trim(),
    fromAddress,
    replyTo: draft.replyTo.trim(),
    organizationName: draft.organizationName.trim(),
    logoUrl: draft.logoUrl.trim(),
    smtpHost: draft.smtpHost.trim(),
    smtpPort,
    smtpSecure: draft.smtpSecure,
    smtpUser: draft.smtpUser.trim(),
    graphTenantId: draft.graphTenantId.trim(),
    graphClientId: draft.graphClientId.trim(),
    graphMailbox,
    inboundMode: draft.inboundMode,
    imapHost: draft.imapHost.trim(),
    imapPort,
    imapSecure: draft.imapSecure,
    imapUser: draft.imapUser.trim(),
    imapMailbox: draft.imapMailbox.trim() || 'INBOX',
  };

  const smtpPassword = secrets.smtpPassword.trim();
  if (smtpPassword) {
    body.smtpPassword = smtpPassword;
  } else if (secrets.clearSmtpPassword && connection.smtpPasswordSet) {
    body.clearSmtpPassword = true;
  }

  const graphSecret = secrets.graphClientSecret.trim();
  if (graphSecret) {
    body.graphClientSecret = graphSecret;
  } else if (secrets.clearGraphClientSecret && connection.graphClientSecretSet) {
    body.clearGraphClientSecret = true;
  }

  const imapPassword = secrets.imapPassword.trim();
  if (imapPassword) {
    body.imapPassword = imapPassword;
  } else if (secrets.clearImapPassword && connection.imapPasswordSet) {
    body.clearImapPassword = true;
  }

  return body;
}

function parsePort(value: string, label: string): number {
  if (!/^\d+$/.test(value.trim())) {
    throw new Error(`${label}: Bitte eine Zahl zwischen 1 und 65535 eingeben.`);
  }
  const parsed = Number.parseInt(value.trim(), 10);
  if (parsed < 1 || parsed > 65535) {
    throw new Error(`${label}: Bitte eine Zahl zwischen 1 und 65535 eingeben.`);
  }
  return parsed;
}
